import React, { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APPROVER_ROLES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReleaseCheckDialog, CheckTypeCell } from "./ReleaseCheckDialog";
import { Panel, DataTable, Fld, StatusPill, DecisionDialog, DecisionCell, money, fmtDate, inputCls } from "./shared";

export const APPROVAL_KINDS = {
  "fund-requests": {
    title: "Operational Fund", noun: "fund request",
    subtitle: "Finance Staff requests funds; Finance Manager or Admin approves or rejects with remarks.",
    fields: [
      { key: "purpose", label: "Purpose", wide: true },
      { key: "amount", label: "Amount", type: "number" },
      { key: "needed_by", label: "Needed by", type: "date" },
      { key: "shipment_number", label: "Shipment # (optional)" },
      { key: "notes", label: "Notes", textarea: true, wide: true },
    ],
    columns: [{ key: "purpose", label: "Purpose" }, { key: "shipment_number", label: "Shipment #", mono: true },
      { key: "needed_by", label: "Needed by", render: (r) => fmtDate(r.needed_by) }],
  },
  "cash-vouchers": {
    title: "Cash Vouchers", noun: "cash voucher",
    subtitle: "Prepared by Finance Staff, approved by Finance Manager or Admin.",
    fields: [
      { key: "payee", label: "Payee" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "date", label: "Date", type: "date" },
      { key: "purpose", label: "Purpose", wide: true },
      { key: "notes", label: "Notes", textarea: true, wide: true },
    ],
    columns: [{ key: "payee", label: "Payee" }, { key: "purpose", label: "Purpose" }, { key: "date", label: "Date", render: (r) => fmtDate(r.date) }],
  },
  "check-vouchers": {
    title: "Check Vouchers", noun: "check voucher",
    subtitle: "Prepared by Finance Staff, approved by Finance Manager or Admin.",
    fields: [
      { key: "payee", label: "Payee" },
      { key: "check_number", label: "Check #" },
      { key: "check_type", label: "Check type", options: [["dated", "Dated (current)"], ["post_dated", "Post-dated"]] },
      { key: "account_id", label: "Deduct from bank account", accounts: "bank", showIf: (f) => f.check_type === "dated" },
      { key: "bank", label: "Bank", showIf: (f) => f.check_type === "post_dated" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "date", label: "Check date", type: "date" },
      { key: "purpose", label: "Purpose (optional)" },
      { key: "notes", label: "Notes", textarea: true, wide: true },
    ],
    defaults: { check_type: "dated" },
    columns: [{ key: "payee", label: "Payee" }, { key: "bank", label: "Bank" }, { key: "check_number", label: "Check #", mono: true },
      { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
      { key: "check_type", label: "Type / deducted from", render: (r) => <CheckTypeCell r={r} /> }],
  },
};

const blank = (cfg) => ({ ...Object.fromEntries(cfg.fields.map((x) => [x.key, ""])), ...(cfg.defaults || {}) });
const shown = (cfg, f) => cfg.fields.filter((x) => !x.showIf || x.showIf(f));

function FieldInput({ x, kind, f, setF, accounts }) {
  const set = (v) => setF({ ...f, [x.key]: v, ...(x.key === "check_type" ? { bank: "", account_id: "" } : {}) });
  const opts = x.accounts ? accounts.filter((a) => a.type === x.accounts).map((a) => [a.id, a.name]) : x.options;
  if (opts) return (
    <Select value={f[x.key]} onValueChange={set}>
      <SelectTrigger data-testid={`input-${kind}-${x.key}`} className={inputCls}><SelectValue placeholder={x.accounts && !opts.length ? "No bank accounts yet" : "Choose"} /></SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return x.textarea
    ? <Textarea data-testid={`input-${kind}-${x.key}`} rows={2} value={f[x.key]} onChange={(e) => set(e.target.value)} className={inputCls} />
    : <Input data-testid={`input-${kind}-${x.key}`} type={x.type || "text"} value={f[x.key]} onChange={(e) => set(e.target.value)} className={inputCls} />;
}

function NewRecordDialog({ kind, cfg, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank(cfg));
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  useEffect(() => {
    if (open && cfg.fields.some((x) => x.accounts)) { api.get("/finance/cash/accounts").then((r) => setAccounts(r.data)).catch(() => {}); }
  }, [open, cfg]);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/finance/${kind}`, { ...f, amount: parseFloat(f.amount) || 0 });
      toast.success(`${data.number} submitted for approval`);
      setOpen(false); setF(blank(cfg)); onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`button-new-${kind}`} className="bg-blue-600 hover:bg-blue-500"><Plus size={14} className="mr-2" /> New {cfg.noun}</Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-xl">
        <DialogHeader><DialogTitle className="font-display text-2xl capitalize">New {cfg.noun}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {shown(cfg, f).map((x) => (
            <Fld key={x.key} label={x.label} className={x.wide ? "sm:col-span-2" : ""}>
              <FieldInput x={x} kind={kind} f={f} setF={setF} accounts={accounts} />
            </Fld>
          ))}
        </div>
        <DialogFooter>
          <Button data-testid={`button-submit-${kind}`} onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-500">
            {busy && <Loader2 size={14} className="mr-2 animate-spin" />} Submit for approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApprovalTab({ kind, onChanged }) {
  const cfg = APPROVAL_KINDS[kind];
  const { user } = useAuth();
  const canApprove = APPROVER_ROLES.includes(user?.role);
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const { data } = await api.get(`/finance/${kind}`); setRows(data); } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  const refresh = () => { load(); onChanged(); };

  const columns = [
    { key: "number", label: "No.", mono: true },
    ...cfg.columns,
    { key: "amount", label: "Amount", right: true, render: (r) => money(r.amount) },
    { key: "created_by_name", label: "Prepared by" },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} testid={`status-${r.number}`} /> },
    { key: "decision", label: "Decision", render: (r) => <DecisionCell r={r} /> },
    { key: "action", label: "", render: (r) => (canApprove && r.status === "Approved" && r.check_type === "post_dated" && !r.release
      ? <ReleaseCheckDialog record={r} onDone={refresh} />
      : canApprove && r.status === "Pending" ? (
      <DecisionDialog kind={kind} record={r} onDone={refresh}>
        {shown(cfg, r).filter((x) => r[x.key]).map((x) => (
          <div key={x.key} className="flex justify-between gap-4 border-b border-slate-800 pb-1">
            <span className="text-slate-500">{x.label}</span>
            <span className="text-right">{x.type === "number" ? money(r[x.key]) : x.accounts ? r.account_name : x.options ? x.options.find(([v]) => v === r[x.key])?.[1] : r[x.key]}</span>
          </div>
        ))}
        {r.items?.map((it, i) => (
          <div key={i} className="flex justify-between gap-4 text-xs text-slate-400 pl-3">
            <span>{it.description}</span><span className="font-mono-ops">{money(it.amount)}</span>
          </div>
        ))}
        <div className="text-xs text-slate-500">Prepared by {r.created_by_name} · {fmtDate(r.created_at)}</div>
      </DecisionDialog>) : null) },
  ];

  return (
    <Panel title={cfg.title} subtitle={cfg.subtitle} right={<NewRecordDialog kind={kind} cfg={cfg} onSaved={refresh} />}>
      <DataTable testid={`${kind}-table`} columns={columns} rows={rows} empty={`No ${cfg.noun}s yet.`} />
    </Panel>
  );
}
